// Eigentümerportal (Portale E6): Datentypen von portal_eigentuemer_daten und reine Anzeige-Regeln
// für Objekt-Umschalter, Übersicht und Einheiten. Nur aus echten Daten abgeleitet – was fehlt, fällt weg.
import { aktuellerStand, leseStand } from '../mieter/mietstaende';
import { istWohnend } from '../dashboard/leerstand';
import { istWeg } from './tabs';

type Zahl = number | string | null;

export interface EigObjekt {
  id: string; bezeichnung: string | null; objekttyp: string | null; strasse: string | null; hausnummer: string | null;
  plz: string | null; stadt: string | null; baujahr: number | null; wohnflaeche: Zahl; verwaltungsart: string | null;
  weg_bestellt_seit: string | null; ganzes_objekt: boolean; einheiten_gesamt: number; mea_gesamt: Zahl;
  ansprechpartner: { name: string | null; email: string | null } | null;
  /** #584: Eigentümer-Datensatz, dem das Objekt gehört (bei mehreren verknüpften Datensätzen) */
  eigentuemer_id?: string | null;
}
export interface EigEinheitEig {
  einheit_id: string; mea: Zahl; hausgeld_vorschuss: Zahl; ruecklage_vorschuss: Zahl; sev_status: string | null;
  vertrag_ab: string | null; vertrag_bis: string | null;
}
export interface EigEinheit {
  id: string; objekt_id: string; bezeichnung: string | null; typ: string | null; etage: string | null;
  wohnflaeche: Zahl; nutzflaeche: Zahl; zimmer: Zahl; uebergeordnete_einheit_id: string | null; leer_seit: string | null;
}
export interface EigMieter {
  einheit_id: string; status: string | null; anrede: string | null; vorname: string | null; nachname: string | null;
  firma_name: string | null; mietbeginn: string | null; mietende: string | null; vertragsart: string | null;
  kaltmiete: Zahl; nebenkosten_vorauszahlung: Zahl; heizkosten: Zahl; weitere_kosten: Zahl; miet_historie: unknown;
}
export type EigPerson = { anrede: string | null; vorname: string | null; nachname: string | null; firma_name: string | null };
export interface EigDaten {
  eigentuemer: EigPerson;
  /** #584: alle mit dem Zugang verknüpften Datensätze (z. B. Privatperson + GmbH) */
  eigentuemer_alle?: (EigPerson & { id: string })[];
  objekte: EigObjekt[];
  objekt_eig: { objekt_id: string; vertrag_ab: string | null; vertrag_bis: string | null }[];
  einheit_eig: EigEinheitEig[];
  einheiten: EigEinheit[];
  mieter: EigMieter[];
}

const zahl = (v: Zahl | undefined): number => (v === null || v === undefined || v === '' ? 0 : Number(v) || 0);
const gerundet = (n: number) => Math.round(n * 100) / 100;

// ── Begrüßung und Objekt ──

/** „Guten Tag, Frau Dr. Brandt“ – Anrede nur, wenn sie gespeichert ist. */
export function begruessung(e: EigDaten['eigentuemer'] | null | undefined): string {
  if (!e) return 'Guten Tag';
  const anrede = (e.anrede ?? '').trim();
  const nachname = (e.nachname ?? '').trim();
  if (/^(frau|herr)/i.test(anrede) && nachname) return `Guten Tag, ${anrede} ${nachname}`;
  const name = [e.vorname, e.nachname].map((x) => (x ?? '').trim()).filter(Boolean).join(' ') || (e.firma_name ?? '').trim();
  return name ? `Guten Tag, ${name}` : 'Guten Tag';
}

export const objektName = (o: Pick<EigObjekt, 'strasse' | 'hausnummer' | 'bezeichnung'>): string =>
  [o.strasse, o.hausnummer].filter(Boolean).join(' ') || (o.bezeichnung ?? '').trim() || 'Objekt';

export type ObjektArt = 'mv' | 'weg' | 'weg_sev' | 'sev';

/** Art der Verwaltung aus objekte.verwaltungsart (+ SEV-Kennzeichen an der eigenen Einheit). */
export function objektArt(o: Pick<EigObjekt, 'verwaltungsart'>, eigene: readonly Pick<EigEinheitEig, 'sev_status'>[]): ObjektArt {
  const v = (o.verwaltungsart ?? '').trim().toUpperCase();
  if (v === 'SEV') return 'sev';
  if (v === 'WEG') return eigene.some((x) => (x.sev_status ?? '').trim()) ? 'weg_sev' : 'weg';
  return 'mv';
}

export const ART_LABEL: Record<ObjektArt, string> = {
  mv: 'Mietverwaltung',
  weg: 'WEG',
  weg_sev: 'WEG · Sondereigentum vermietet',
  sev: 'Sondereigentumsverwaltung',
};

export const artIstWeg = (a: ObjektArt): boolean => a !== 'mv';

// ── Einheiten ──

export type EinheitStatus = 'vermietet' | 'leer' | 'gekuendigt' | 'gewerbe' | 'ohne';

export interface EinheitSicht {
  id: string;
  bezeichnung: string;
  lage: string;
  status: EinheitStatus;
  mieterName: string | null;
  mieterSeit: string | null;
  fakten: [string, string][];
  hinweis: string | null;
  kaltmiete: number;
}

const DATUM = (iso: string) => new Date(`${iso.slice(0, 10)}T12:00:00`).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
const MONAT_JAHR = (iso: string) => { const d = new Date(`${iso.slice(0, 10)}T12:00:00`); return `${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`; };
export const zahlDe = (n: number, stellen = 1): string => n.toLocaleString('de-DE', { maximumFractionDigits: stellen });
export const euroGanz = (n: number): string => n.toLocaleString('de-DE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 });

/** Mietverhältnis läuft heute (begonnen, nicht beendet, Status wohnend). */
export function laeuftHeute(m: Pick<EigMieter, 'status' | 'mietbeginn' | 'mietende'>, heute: string): boolean {
  if (!istWohnend(m.status)) return false;
  if (m.mietbeginn && m.mietbeginn.slice(0, 10) > heute) return false;
  if (m.mietende && m.mietende.slice(0, 10) < heute) return false;
  return true;
}

/** Aktuelle Kaltmiete aus dem gültigen Mietstand, sonst aus dem Feld. */
export function kaltmieteAktuell(m: Pick<EigMieter, 'kaltmiete' | 'miet_historie'>, heute: string): number {
  const historie = Array.isArray(m.miet_historie) ? (m.miet_historie as unknown[]).map((r, i) => leseStand(r, i)) : [];
  const stand = aktuellerStand(historie, heute);
  return gerundet(stand ? stand.kaltmiete : zahl(m.kaltmiete));
}

export function mieterName(m: Pick<EigMieter, 'vorname' | 'nachname' | 'firma_name'>): string {
  return (m.firma_name ?? '').trim() || [m.vorname, m.nachname].map((x) => (x ?? '').trim()).filter(Boolean).join(' ') || 'Mieter';
}

const istGewerbe = (e: Pick<EigEinheit, 'typ'>, m: Pick<EigMieter, 'vertragsart'>[]): boolean =>
  /gewerb|laden|büro|buero|praxis/i.test(e.typ ?? '') || m.some((x) => /gewerb/i.test(x.vertragsart ?? ''));

export interface EinheitKontext {
  art: ObjektArt;
  eig?: EigEinheitEig;
  meaGesamt: number;
  /** Wir vermieten für den Eigentümer (ganzes Objekt oder SEV) – sonst kein Vermietungsstand. */
  mieterSichtbar: boolean;
  /** Zimmer dieser Wohnung (#499): ein vermietetes Zimmer belegt die Wohnung. */
  zimmerIds?: ReadonlySet<string>;
  heute: string;
}

function lageText(e: EigEinheit, k: EinheitKontext, mea: number): string {
  const teile = [(e.etage ?? '').trim()];
  if (artIstWeg(k.art) && mea > 0) teile.push(k.meaGesamt > 0 ? `${zahlDe(mea, 2)} / ${zahlDe(k.meaGesamt, 2)} MEA` : `${zahlDe(mea, 2)} MEA`);
  return teile.filter(Boolean).join(' · ');
}

function faktenText(e: EigEinheit, k: EinheitKontext, kaltmiete: number, hausgeld: number): [string, string][] {
  const weg = artIstWeg(k.art);
  const flaeche = zahl(e.wohnflaeche) || zahl(e.nutzflaeche);
  const fakten: [string, string][] = [];
  if (flaeche > 0) fakten.push(['Fläche', `${zahlDe(flaeche)} m²`]);
  if (!weg && zahl(e.zimmer) > 0) fakten.push(['Zimmer', zahlDe(zahl(e.zimmer))]);
  if (k.mieterSichtbar && kaltmiete > 0) fakten.push(['Kaltmiete', euroGanz(kaltmiete)]);
  if (weg && hausgeld > 0) fakten.push(['Hausgeld', euroGanz(hausgeld)]);
  return fakten;
}

const nachBeginn = (a: EigMieter, b: EigMieter) => (a.mietbeginn ?? '').localeCompare(b.mietbeginn ?? '');
const istGekuendigt = (m: EigMieter) => /gek(ü|ue)ndigt/i.test(m.status ?? '');

/** Vermietungsstand aus den laufenden Mietverhältnissen. */
function vermietung(e: EigEinheit, laufend: EigMieter[]): Pick<EinheitSicht, 'status' | 'mieterName' | 'mieterSeit' | 'hinweis'> {
  if (!laufend.length) return { status: 'leer', mieterName: null, mieterSeit: e.leer_seit ? `seit ${DATUM(e.leer_seit)}` : null, hinweis: null };
  const erster = [...laufend].sort(nachBeginn)[0];
  const einName = laufend.every((m) => mieterName(m) === mieterName(erster));
  const name = einName ? mieterName(erster) : `${mieterName(erster)} u. a.`;
  const ende = laufend.map((m) => m.mietende ?? '').filter(Boolean).sort((a, b) => a.localeCompare(b)).pop();
  const gewerbe = istGewerbe(e, laufend);
  const seit = erster.mietbeginn ? `${gewerbe ? 'Gewerbe seit' : 'seit'} ${MONAT_JAHR(erster.mietbeginn)}` : null;
  // Gekündigt nur nach Status; ein Enddatum allein kann auch ein befristeter Vertrag sein.
  if (laufend.every(istGekuendigt)) {
    return { status: 'gekuendigt', mieterName: name, mieterSeit: seit, hinweis: ende ? `Gekündigt zum ${DATUM(ende)}.` : 'Gekündigt.' };
  }
  const befristet = ende && laufend.every((m) => !!m.mietende) ? `Mietverhältnis endet am ${DATUM(ende)}.` : null;
  return { status: gewerbe ? 'gewerbe' : 'vermietet', mieterName: name, mieterSeit: seit, hinweis: befristet };
}

/** Sicht einer Einheit: Lage, Kennzahlen und – wo wir vermieten – der Vermietungsstand. */
export function einheitSicht(e: EigEinheit, alleMieter: readonly EigMieter[], k: EinheitKontext): EinheitSicht {
  const ids = new Set([e.id, ...(k.zimmerIds ?? [])]);
  const laufend = alleMieter.filter((m) => ids.has(m.einheit_id) && laeuftHeute(m, k.heute));
  const kaltmiete = k.mieterSichtbar ? gerundet(laufend.reduce((s, m) => s + kaltmieteAktuell(m, k.heute), 0)) : 0;
  const mea = k.eig ? zahl(k.eig.mea) : 0;
  const hausgeld = k.eig ? gerundet(zahl(k.eig.hausgeld_vorschuss) + zahl(k.eig.ruecklage_vorschuss)) : 0;
  const basis = {
    id: e.id, bezeichnung: (e.bezeichnung ?? '').trim() || 'Einheit', lage: lageText(e, k, mea),
    fakten: faktenText(e, k, kaltmiete, hausgeld), kaltmiete,
  };
  if (!k.mieterSichtbar) return { ...basis, status: 'ohne', mieterName: null, mieterSeit: null, hinweis: null };
  return { ...basis, ...vermietung(e, laufend) };
}

export interface ObjektSicht {
  objekt: EigObjekt;
  name: string;
  art: ObjektArt;
  einheiten: EinheitSicht[];
  /** Summen über die sichtbaren Einheiten */
  flaeche: number;
  kaltmiete: number;
  leer: number;
  quote: number | null;
  /** WEG: eigene MEA und Hausgeld */
  mea: number;
  meaGesamt: number;
  hausgeld: number;
  mieterSichtbar: boolean;
}

export function objektSicht(d: EigDaten, objektId: string, heute: string): ObjektSicht | null {
  const o = d.objekte.find((x) => x.id === objektId);
  if (!o) return null;
  const einheitenObj = d.einheiten.filter((e) => e.objekt_id === o.id);
  const eigJe = new Map(d.einheit_eig.map((x) => [x.einheit_id, x]));
  const eigene = einheitenObj.map((e) => eigJe.get(e.id)).filter((x): x is EigEinheitEig => !!x);
  const art = objektArt(o, eigene);
  const meaGesamt = zahl(o.mea_gesamt);
  const zimmer = new Map<string, Set<string>>();
  for (const e of einheitenObj) {
    if (!e.uebergeordnete_einheit_id) continue;
    zimmer.set(e.uebergeordnete_einheit_id, new Set([...(zimmer.get(e.uebergeordnete_einheit_id) ?? []), e.id]));
  }
  const mieterSichtbar = o.ganzes_objekt || art === 'sev' || art === 'weg_sev';
  const wohnungen = einheitenObj.filter((e) => !e.uebergeordnete_einheit_id || !einheitenObj.some((x) => x.id === e.uebergeordnete_einheit_id));
  const einheiten = wohnungen
    .map((e) => einheitSicht(e, d.mieter, { art, eig: eigJe.get(e.id), meaGesamt, mieterSichtbar, zimmerIds: zimmer.get(e.id), heute }))
    // Wohnungen zuerst, Gewerbe danach (wie im Prototyp), sonst nach Bezeichnung
    .sort((a, b) => Number(a.status === 'gewerbe') - Number(b.status === 'gewerbe') || a.bezeichnung.localeCompare(b.bezeichnung, 'de', { numeric: true }));
  const leer = einheiten.filter((e) => e.status === 'leer').length;
  const flaeche = gerundet(wohnungen.reduce((s, e) => s + (zahl(e.wohnflaeche) || zahl(e.nutzflaeche)), 0));
  return {
    objekt: o, name: objektName(o), art, einheiten, flaeche,
    kaltmiete: gerundet(einheiten.reduce((s, e) => s + e.kaltmiete, 0)),
    leer,
    quote: art === 'mv' && mieterSichtbar && einheiten.length ? Math.round(((einheiten.length - leer) / einheiten.length) * 100) : null,
    mea: gerundet(eigene.reduce((s, x) => s + zahl(x.mea), 0)),
    meaGesamt,
    hausgeld: gerundet(eigene.reduce((s, x) => s + zahl(x.hausgeld_vorschuss) + zahl(x.ruecklage_vorschuss), 0)),
    mieterSichtbar,
  };
}

/** Anzeigename eines Datensatzes: Firma vor Vor- und Nachname. */
export function personName(e: EigPerson): string {
  return (e.firma_name ?? '').trim() || [e.vorname, e.nachname].map((x) => (x ?? '').trim()).filter(Boolean).join(' ');
}

/** #584: „über MR Wohnraum GmbH“ – nur wenn mehrere Datensätze am Zugang hängen. */
export function ueberZeile(d: Pick<EigDaten, 'eigentuemer_alle'>, o: Pick<EigObjekt, 'eigentuemer_id'>): string | null {
  const alle = d.eigentuemer_alle ?? [];
  if (alle.length < 2 || !o.eigentuemer_id) return null;
  const e = alle.find((x) => x.id === o.eigentuemer_id);
  const name = e ? personName(e) : '';
  return name ? `über ${name}` : null;
}

/** #584: Vorauswahl der Objekte zeigen, solange bei mehreren Objekten in dieser Sitzung noch keins gewählt ist. */
export function auswahlZeigen(anzahlObjekte: number, gewaehlt: boolean): boolean {
  return anzahlObjekte > 1 && !gewaehlt;
}

/** Zweite Zeile im Objekt-Umschalter: „Mietverwaltung · 6 Einheiten“ bzw. „WEG · WE 03, vermietet“. */
export function umschalterZeile(s: ObjektSicht): string {
  if (!artIstWeg(s.art)) return `${ART_LABEL.mv} · ${s.einheiten.length} ${s.einheiten.length === 1 ? 'Einheit' : 'Einheiten'}`;
  const namen = s.einheiten.map((e) => e.bezeichnung).join(', ');
  const vermietet = s.mieterSichtbar && s.einheiten.length > 0 && s.einheiten.every((e) => e.status !== 'leer');
  return ['WEG', [namen, vermietet ? 'vermietet' : ''].filter(Boolean).join(', ')].filter(Boolean).join(' · ');
}

/** Infozeile unter der Adresse. */
export function objektInfo(s: ObjektSicht, objektEig: EigDaten['objekt_eig']): string {
  const o = s.objekt;
  if (artIstWeg(s.art)) {
    const teile = [o.einheiten_gesamt > 0 ? `WEG mit ${o.einheiten_gesamt} Einheiten` : 'WEG'];
    if (s.mea > 0) teile.push(s.meaGesamt > 0 ? `Ihr Anteil ${zahlDe(s.mea, 2)} / ${zahlDe(s.meaGesamt, 2)} MEA` : `Ihr Anteil ${zahlDe(s.mea, 2)} MEA`);
    return teile.join(' · ');
  }
  const ab = objektEig.find((x) => x.objekt_id === o.id)?.vertrag_ab;
  return [
    (o.objekttyp ?? '').trim(),
    o.baujahr ? `Baujahr ${o.baujahr}` : '',
    ab ? `seit ${ab.slice(0, 4)} in Verwaltung` : '',
  ].filter(Boolean).join(' · ');
}

export function objektAdresse(s: ObjektSicht): string {
  const o = s.objekt;
  const strasse = [o.strasse, o.hausnummer].filter(Boolean).join(' ');
  const einheit = artIstWeg(s.art) && s.einheiten.length === 1 ? s.einheiten[0].bezeichnung : '';
  return [strasse, einheit, [o.plz, o.stadt].filter(Boolean).join(' ')].filter(Boolean).join(', ');
}

/** Tabs für das gewählte Objekt: Versammlung bleibt aus (nicht in diesem Auftrag, Entscheidung 22.10.2026). */
export const tabOptionen = (o: Pick<EigObjekt, 'verwaltungsart'> | null): { weg: boolean; versammlung: boolean } =>
  ({ weg: istWeg(o?.verwaltungsart), versammlung: false });
